import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

// ---------------------------------------------------------------------------
// Per-user dashboard customization — wraps the new
// /user-preferences/dashboard endpoints on the DM API.
//
// Legacy parity: DashboardCustomization.aspx (CustomGridEdit.ascx) +
// UserPreference.DashboardPagination. The legacy app persists a CLOB XML
// blob; v1 the API in-memory store fronts a future Oracle CLOB hook.
//
// All three endpoints scope to the current request principal — the SPA
// never sends a user id explicitly. In dev that's whoever the
// ImpersonationSwitcher has selected; in prod it's the AAD principal.
// ---------------------------------------------------------------------------

export interface DashboardColumnDefinition {
  dataField: string;
  headerText: string;
  width: number | null;
  isDefault: boolean;
}

export interface DashboardCustomization {
  visibleColumns: string[];
  defaultStatusFilter: string[];
  defaultBannerCode: string | null;
  defaultCategoryManagerCodes: number[];
  pageSize: number;
  sortColumn: string;
  sortAscending: boolean;
}

export interface DashboardCustomizationResponse {
  qualifiedUserId: string;
  isCustomized: boolean;
  customization: DashboardCustomization;
}

export interface DashboardCatalog {
  columns: DashboardColumnDefinition[];
  statuses: string[];
  sortColumns: string[];
  pageSizes: number[];
}

const BASE = "/dm/V1/user-preferences/dashboard";
const QK = {
  prefs: ["dashboard-prefs"] as const,
  catalog: ["dashboard-prefs-catalog"] as const,
};

/**
 * Returns the saved customization (or the server-supplied defaults). The
 * `customization` block is always populated; `isCustomized` flags whether
 * the user has explicitly saved anything yet.
 */
export const useDashboardPreferences = () =>
  useQuery({
    queryKey: QK.prefs,
    queryFn: async (): Promise<DashboardCustomizationResponse> => {
      const response = await apiClient.get<DashboardCustomizationResponse>(BASE);
      return response.data;
    },
    // Identity-scoped + tiny — cache for the session.
    staleTime: 5 * 60 * 1000,
  });

/** Catalog of available columns / statuses / sort options. */
export const useDashboardPreferencesCatalog = () =>
  useQuery({
    queryKey: QK.catalog,
    queryFn: async (): Promise<DashboardCatalog> => {
      const response = await apiClient.get<DashboardCatalog>(
        `${BASE}/catalog`
      );
      return response.data;
    },
    staleTime: 60 * 60 * 1000, // catalog is static
  });

/** Save the customization. PUT returns the persisted shape — we replace the cached query data. */
export const useUpdateDashboardPreferences = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: DashboardCustomization
    ): Promise<DashboardCustomizationResponse> => {
      const response = await apiClient.put<DashboardCustomizationResponse>(
        BASE,
        body
      );
      return response.data;
    },
    onSuccess: (resp) => {
      qc.setQueryData(QK.prefs, resp);
    },
  });
};

/** Reset to server defaults. DELETE returns 204; we invalidate so the next render refetches defaults. */
export const useResetDashboardPreferences = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete(BASE);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.prefs });
    },
  });
};
